import { useState } from "react";
import { Link } from "react-router-dom";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import Loading from "../../components/Loading";

const money = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;

function UpgradeCard({ u, budget, busy, onBuy }) {
  const levelText =
    u.kind === "chain"
      ? u.level > 0
        ? `Stufe ${u.level}/${u.maxLevel}${u.levelLabel ? ` · ${u.levelLabel}` : ""}`
        : `Noch nicht gebaut · bis Stufe ${u.maxLevel}`
      : u.owned
        ? "Gebaut"
        : "Einzelstück";

  return (
    <div
      className={`rounded-2xl border p-4 ${
        u.maxed
          ? "border-emerald-400 bg-emerald-50/80 dark:bg-emerald-950/20"
          : u.available
            ? "border-gray-300 bg-white dark:bg-gray-700"
            : "border-dashed opacity-70 bg-gray-50 dark:bg-gray-800"
      }`}
    >
      <div className="flex justify-between gap-2 items-start">
        <div>
          <div className="font-bold">{u.label}</div>
          <div className="text-xs font-semibold opacity-70 mt-0.5">{levelText}</div>
          <p className="text-sm opacity-75 mt-1">{u.blurb}</p>
          {u.nextLevel ? (
            <p className="text-xs opacity-70 mt-2">
              Als Nächstes: <span className="font-semibold">{u.nextLevel.label}</span>
              {u.nextLevel.blurb ? ` — ${u.nextLevel.blurb}` : ""}
            </p>
          ) : null}
        </div>
        <div className="font-black whitespace-nowrap text-right">
          {u.available ? money(u.cost) : u.maxed ? "Max" : "—"}
        </div>
      </div>
      {u.maxed ? (
        <div className="mt-3 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
          {u.kind === "chain" ? "Maximale Stufe" : "Gebaut"}
        </div>
      ) : u.available ? (
        <button
          type="button"
          disabled={Boolean(busy) || budget < u.cost}
          className="mt-3 px-4 py-2 rounded-xl bg-gray-900 text-white font-semibold disabled:opacity-40"
          onClick={() => onBuy(u)}
        >
          {busy === u.key
            ? "…"
            : u.kind === "chain" && u.level > 0
              ? `Auf Stufe ${u.level + 1} für ${money(u.cost)}`
              : `Bauen für ${money(u.cost)}`}
        </button>
      ) : (
        <div className="mt-3 text-xs opacity-70">{u.lockedReason || "Gesperrt"}</div>
      )}
    </div>
  );
}

const ManagerVenuePage = () => {
  const { userId, manager, loading, refresh } = useVirtualWorld();
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(true);
  const [busy, setBusy] = useState("");
  const [nameDraft, setNameDraft] = useState("");

  if (loading && !manager) return <Loading />;
  if (!manager?.club) return <p>Bitte zuerst einen Club gründen.</p>;

  const venue = manager.venue;
  if (!venue) {
    return (
      <p>
        Heimstätte lädt noch nicht — bitte Seite neu laden oder{" "}
        <Link className="underline" to="/manager">
          zum Club
        </Link>
        .
      </p>
    );
  }

  const fin = manager.dashboard?.finance || {};
  const budget = Number(fin.budget ?? manager.club.budget ?? 0);
  const wages = Number(fin.wageBill ?? manager.club.wageBill ?? 0);
  const crowdFlat = Number(venue.effects?.crowdFlat || 0);
  const crowdMult = Number(venue.effects?.crowdMult || 0);
  const sponsorMult = Number(venue.effects?.sponsorMult || 0);
  const nameValue = nameDraft || venue.customName || "";
  const chains = venue.chains || (venue.upgrades || []).filter((u) => u.kind === "chain");
  const items = venue.items || (venue.upgrades || []).filter((u) => u.kind !== "chain");

  const run = async (key, fn) => {
    setBusy(key);
    setMsg("");
    try {
      await fn();
      await refresh();
      setOk(true);
      setMsg("Erledigt.");
    } catch (err) {
      setOk(false);
      setMsg(err?.data?.message || err?.message || "Aktion fehlgeschlagen.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <section className="rounded-2xl border border-emerald-400/50 bg-emerald-50 dark:bg-emerald-950/25 p-4">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-70">Club-Finanzen · Bauentscheidung</div>
            <h2 className="text-2xl font-black">Kontostand {money(budget)}</h2>
          </div>
          <Link to="/manager/club" className="text-sm font-semibold underline opacity-80">
            Zur Geschäftsstelle
          </Link>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-xl bg-white/70 dark:bg-black/20 p-3">
            <div className="text-xs opacity-60">Gehälter / Spieltag</div>
            <div className="font-black text-lg text-red-800 dark:text-red-300">−{money(wages)}</div>
          </div>
          <div className="rounded-xl bg-white/70 dark:bg-black/20 p-3">
            <div className="text-xs opacity-60">Heim-Bonus (Venue)</div>
            <div className="font-black text-lg text-emerald-800 dark:text-emerald-300">
              {crowdFlat || crowdMult
                ? `+${money(crowdFlat)}${crowdMult ? ` · +${Math.round(crowdMult * 100)} %` : ""}`
                : "noch keiner"}
            </div>
            <div className="text-xs opacity-70 mt-1">
              {sponsorMult ? `Sponsor Heim +${Math.round(sponsorMult * 100)} %` : "ohne Sponsor-Boost"}
            </div>
          </div>
          <div className="rounded-xl bg-white/70 dark:bg-black/20 p-3">
            <div className="text-xs opacity-60">Nächster Ausbau</div>
            <div className="font-black text-lg">
              {venue.nextTier ? money(venue.nextTier.cost) : "Arena erreicht"}
            </div>
            <div className="text-xs opacity-70 mt-1">
              {venue.nextTier
                ? budget >= venue.nextTier.cost
                  ? "Budget reicht"
                  : `fehlen ${money(venue.nextTier.cost - budget)}`
                : "Upgrades unten prüfen"}
            </div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-amber-700/30 shadow-lg">
        <div
          className="relative min-h-[200px] bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(120deg, rgba(20,12,8,.9), rgba(40,24,12,.55)), url(${
              venue.imageUrl ||
              "https://images.unsplash.com/photo-1579019163248-e7761241d85a?auto=format&fit=crop&w=1400&q=80"
            })`,
          }}
        >
          <div className="relative z-10 p-6 md:p-8 text-amber-50">
            <div className="text-xs uppercase tracking-[0.18em] opacity-80 mb-2">Heimstätte</div>
            {venue.statusLabel || venue.tier?.statusLabel ? (
              <div className="inline-flex text-xs uppercase tracking-[0.18em] px-3 py-1 rounded-full bg-amber-200/15 border border-amber-100/30 mb-3">
                {venue.statusLabel || venue.tier?.statusLabel}
              </div>
            ) : null}
            <h1 className="text-3xl md:text-4xl font-black mb-2">{venue.name}</h1>
            <p className="opacity-90 mb-3">
              {venue.tier?.label} · {venue.atmosphereLabel} · {venue.capacity} Plätze
            </p>
            <p className="text-sm opacity-80 max-w-xl">
              Gebäude-Stufen bringen mehr Plätze und Heim-/Sponsor-Einnahmen; Ketten (Lvl 1–3) und Einzelstücke
              oben drauf. Mit dem Startbudget reicht es nur für den Anfang — Arena und Max-Stufen über die Saison.
            </p>
          </div>
        </div>
      </section>

      {msg ? (
        <p className={`p-3 rounded font-semibold ${ok ? "bg-emerald-100 text-emerald-900" : "bg-red-100 text-red-800"}`}>
          {msg}
        </p>
      ) : null}

      <section className="bg-white dark:bg-gray-700 rounded-2xl p-5 shadow space-y-3">
        <h2 className="font-bold text-xl">Aktive Perks</h2>
        <ul className="text-sm space-y-1">
          {(venue.perks || []).map((line) => (
            <li key={line}>• {line}</li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2 items-end pt-2">
          <label className="flex-1 min-w-[200px]">
            <span className="text-xs opacity-60">Name der Spielstätte</span>
            <input
              className="w-full mt-1 px-3 py-2 rounded border bg-transparent"
              value={nameValue}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder={`${manager.club.name} · Heim`}
              maxLength={48}
            />
          </label>
          <button
            type="button"
            disabled={Boolean(busy)}
            className="px-4 py-2 rounded-xl bg-gray-900 text-white font-semibold"
            onClick={() => run("rename", () => vw.renameVenue(userId, nameValue))}
          >
            {busy === "rename" ? "…" : "Namen speichern"}
          </button>
        </div>
      </section>

      {venue.nextTier ? (
        <section className="rounded-2xl border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/20 p-5">
          <div className="text-xs uppercase tracking-wide opacity-70 mb-1">Gebäude-Stufe</div>
          <h2 className="text-2xl font-black mb-1">{venue.nextTier.label}</h2>
          {venue.nextTier.statusLabel ? (
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">
              Club-Badge wird: {venue.nextTier.statusLabel}
            </p>
          ) : null}
          <p className="text-sm opacity-80 mb-2">{venue.nextTier.blurb}</p>
          <p className="font-semibold mb-1">
            {venue.nextTier.capacity} Plätze Basis · {money(venue.nextTier.cost)}
          </p>
          {venue.nextTier.effects ? (
            <p className="text-sm opacity-80 mb-3">
              Heim-Einnahmen
              {venue.nextTier.effects.crowdFlat
                ? ` +${Number(venue.nextTier.effects.crowdFlat).toLocaleString("de-DE")} €`
                : ""}
              {venue.nextTier.effects.crowdMult
                ? ` · +${Math.round(Number(venue.nextTier.effects.crowdMult) * 100)} % Tickets`
                : ""}
              {venue.nextTier.effects.sponsorMult
                ? ` · +${Math.round(Number(venue.nextTier.effects.sponsorMult) * 100)} % Sponsor`
                : ""}
              {" "}
              (bei Heimspielen)
            </p>
          ) : (
            <div className="mb-3" />
          )}
          <button
            type="button"
            disabled={Boolean(busy) || budget < venue.nextTier.cost}
            className="px-5 py-3 rounded-xl bg-amber-700 text-white font-bold disabled:opacity-40"
            onClick={() => run("tier", () => vw.buyVenueTier(userId, venue.nextTier.key))}
          >
            {busy === "tier" ? "…" : `Ausbauen für ${money(venue.nextTier.cost)}`}
          </button>
        </section>
      ) : (
        <section className="rounded-2xl border border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20 p-5">
          <h2 className="text-xl font-black">Arena erreicht</h2>
          <p className="text-sm opacity-80">Höchste Gebäude-Stufe — Feinschliff über Ketten und Einzelstücke.</p>
        </section>
      )}

      <section className="space-y-3">
        <div>
          <h2 className="font-bold text-xl">Ausbaubare Einrichtungen</h2>
          <p className="text-sm opacity-70">Jede Kette hat Stufe 1–3. Höhere Stufen brauchen oft ein größeres Gebäude.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {chains.map((u) => (
            <UpgradeCard
              key={u.key}
              u={u}
              budget={budget}
              busy={busy}
              onBuy={(item) => run(item.key, () => vw.buyVenueUpgrade(userId, item.key))}
            />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="font-bold text-xl">Einzelstücke</h2>
          <p className="text-sm opacity-70">Einmal kaufen — kleine, gezielte Perks neben den großen Ketten.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((u) => (
            <UpgradeCard
              key={u.key}
              u={u}
              budget={budget}
              busy={busy}
              onBuy={(item) => run(item.key, () => vw.buyVenueUpgrade(userId, item.key))}
            />
          ))}
        </div>
      </section>

      <p className="text-sm opacity-70">
        Sponsor und Büro bleiben unter{" "}
        <Link className="underline font-semibold" to="/manager/club">
          Geschäftsstelle
        </Link>
        .
      </p>
    </div>
  );
};

export default ManagerVenuePage;
